import React, { useEffect, useRef, useState, ReactNode } from "react";
import "./ActionsMenu.scss";
import type { Placement } from "@floating-ui/react";
import Tooltip from "../Tooltip/Tooltip";
import SeemPopover from "../SeemPopover/SeemPopover";
import {
  getDataAnalyticsProps,
  type DataAnalyticsAttributes,
} from "../../../utils/UserAnalyticsUtils";

export interface ActionsMenuItemTooltipData {
  place?: Placement;
  dataTip?: ReactNode;
  className?: string;
}

export interface ActionsMenuItem extends DataAnalyticsAttributes {
  text?: string;
  component?: ReactNode;
  items?: ActionsMenuItem[];
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  unselectable?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  selected?: boolean;
  isSeparator?: boolean;
  tooltipData?: ActionsMenuItemTooltipData;
  "data-testid"?: string;
}

export interface ActionsMenuProps extends DataAnalyticsAttributes {
  menuItems: ActionsMenuItem[];
  children?: ReactNode;
  className?: string;
  megaMenu?: boolean;
  "data-testid"?: string;
  onMenuOpenChange?: ((isOpen: boolean) => void) | null;
  title?: string;
  cleanListBg?: boolean;
  disabled?: boolean;
  initialIsOpen?: boolean;
  allowPropagation?: boolean;
  place?: Placement;
  triggerClassName?: string;
  useSameWidth?: boolean;
  isHidden?: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  menuItems,
  children,
  className,
  megaMenu,
  "data-testid": dataTestId,
  onMenuOpenChange = null,
  title,
  cleanListBg,
  disabled,
  initialIsOpen = false,
  allowPropagation = false,
  place = "bottom-end",
  triggerClassName,
  useSameWidth,
  ...props
}) => {
  const [displayedItems, setDisplayedItems] =
    useState<ActionsMenuItem[]>(menuItems);
  const [subMenuTitle, setSubMenuTitle] = useState("");
  const [isOpen, toggleIsOpen] = useState(initialIsOpen);
  const actionsMenuWrapperRef = useRef<HTMLDivElement>(null);

  const resetSubMenu = () => {
    setDisplayedItems(menuItems);
    setSubMenuTitle("");
  };

  useEffect(() => {
    setDisplayedItems(menuItems);
  }, [menuItems]);

  useEffect(() => {
    onMenuOpenChange && onMenuOpenChange(isOpen);
    // Clear on unmount / before the effect re-runs so this deferred state update
    // can't fire after the component (or a test's jsdom env) is torn down —
    // which otherwise throws "window is not defined" and fails the test run.
    const resetTimer = setTimeout(() => resetSubMenu(), 200);
    return () => clearTimeout(resetTimer);
  }, [isOpen]);

  const getMenu = () => {
    const actions = displayedItems.map((item, index) => (
      <>
        {item.isSeparator ? (
          <div className="actions-menu-separator" key={index} />
        ) : (
          <Tooltip
            placement={item?.tooltipData?.place || "left"}
            content={item?.tooltipData?.dataTip}
            className={item?.tooltipData?.className}
            type="info"
          >
            <li
              data-testid={item?.["data-testid"]}
              className={`${item.className ? item.className : "actions-menu-li"} ${item.isSelected ? "is-selected" : ""} ${item?.disabled ? "is-disabled" : ""} ${item?.unselectable ? "is-unselectable" : ""} ${item?.selected ? "is-selected" : ""}`}
              key={`${item.text}-${index}`}
              {...getDataAnalyticsProps(item)}
              onClick={() => {
                if (item.disabled || item.unselectable) {
                  return;
                }
                if (!item?.items) {
                  toggleIsOpen(false);
                  item?.onClick?.();
                } else {
                  setDisplayedItems(item?.items);
                  setSubMenuTitle(item?.text ?? "");
                }
              }}
            >
              {
                <div
                  className={`actions-menu-li-item-wrap ${
                    item.isDisabled ? "is-disabled" : ""
                  }`}
                >
                  {item.component || item.text}
                  {!!item?.items?.length && (
                    <div className="actions-menu-li-item-expand-icon-wrap">
                      <i className="actions-menu-li-item-expand-icon seem-icon-right-arrow" />
                    </div>
                  )}
                </div>
              }
            </li>
          </Tooltip>
        )}
      </>
    ));

    return (
      <ul
        key="actions-menu-list"
        className={`actions-menu-ul ${cleanListBg ? "clean-list-bg" : ""}`}
        data-testid={`${dataTestId}-list`}
      >
        {actions}
      </ul>
    );
  };
  useEffect(() => {
    if (onMenuOpenChange) {
      onMenuOpenChange(isOpen);
    }
  }, [isOpen]);

  return (
    <SeemPopover
      isOpen={isOpen}
      placement={place}
      onOuterAction={() => toggleIsOpen(false)}
      className={`actions-menu-popover ${className ? className : ""}-popover`}
      triggerClassName={triggerClassName}
      useSameWidth={useSameWidth}
      body={[
        <div
          className={`actions-menu ${megaMenu ? "actions-menu-mega" : ""} ${
            className ? className : ""
          }`}
          data-testid={`${dataTestId}-wrapper`}
        >
          {!!title && <label className={`actions-menu-title`}>{title}</label>}
          {!!subMenuTitle && (
            <div
              className={"actions-menu-sub-menu-title"}
              data-testid={"actions-menu-sub-menu-title-back-button"}
              onClick={resetSubMenu}
            >
              <i className="seem-icon-left-arrow" title={"Back"} />
              {subMenuTitle}
            </div>
          )}
          {getMenu()}
        </div>,
      ]}
    >
      <div
        ref={actionsMenuWrapperRef}
        className="actions-menu-wrapper"
        data-testid={dataTestId}
        {...getDataAnalyticsProps(props)}
        onClick={(event) => {
          if (!disabled) {
            !allowPropagation && event.stopPropagation();
            toggleIsOpen((prevState) => !prevState);
          }
        }}
      >
        {!!children ? (
          children
        ) : (
          <i className={`seem-icon seem-icon-more-actions`} />
        )}
      </div>
    </SeemPopover>
  );
};

export default ActionsMenu;
